import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import useAuth from 'hooks/useAuth'
import { LocalAuthStorageKey } from 'types'

export default function useSessionTimeout(limitMs: number): void {
  const location = useLocation()
  const { profile } = useAuth()
  const lastActivityWriteRef = useRef<number>(0)

  const effectiveLimit: number = limitMs
  const activityThrottleMs = 60_000

  const skipRoutes: string[] = [
    '/session-expired-verify-email',
    '/session-expired-otp-verify'
  ]

  const shouldSkip: boolean = skipRoutes.includes(location.pathname)

  useEffect(() => {
    if (shouldSkip) return

    const updateActivity = () => {
      if (!profile) return

      const now = Date.now()
      if (now - lastActivityWriteRef.current < activityThrottleMs) {
        return
      }

      const lastRaw = localStorage.getItem('last_activity')
      const last = Number(lastRaw)
      if (last && now - last < activityThrottleMs) {
        lastActivityWriteRef.current = last
        return
      }

      localStorage.setItem('last_activity', now.toString())
      lastActivityWriteRef.current = now
    }

    lastActivityWriteRef.current =
      Number(localStorage.getItem('last_activity')) || 0

    window.addEventListener('click', updateActivity)
    window.addEventListener('keypress', updateActivity)
    window.addEventListener('scroll', updateActivity)

    return () => {
      window.removeEventListener('click', updateActivity)
      window.removeEventListener('keypress', updateActivity)
      window.removeEventListener('scroll', updateActivity)
    }
  }, [shouldSkip, profile])

  useEffect(() => {
    if (shouldSkip) return

    const checkTimeout = () => {
      const lastRaw = localStorage.getItem('last_activity')
      if (!lastRaw) return

      const last = Number(lastRaw)
      if (!last) return

      const diff = Date.now() - last
      if (diff >= effectiveLimit) {
        const currentPath = window.location.href
        localStorage.setItem(
          LocalAuthStorageKey.PREVIOUS_SESSION_EXPIRED_PATH,
          currentPath
        )
        window.location.href = '/session-expired-verify-email'
      }
    }

    checkTimeout()

    const interval = window.setInterval(checkTimeout, 5000)

    return () => clearInterval(interval)
  }, [shouldSkip, effectiveLimit])
}
